import { FC, useEffect, useRef, useState } from "react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { Card } from "shared/components/Card";
import { DateInput } from "shared/components/forms/DateInput";
import { Radio } from "shared/components/forms/Radio";
import DatePickerComp from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate } from "shared/components/DateRangeSelect/formatDate";
import {
  MODULE_DATE_PRESETS,
  moduleDateRangeLabel,
  type ModuleDateRangePreset,
  type ModuleDateRangeValue
} from "./moduleDateRange";

const CUSTOM_VALUE = "custom";

const parseDate = (str: string): Date => new Date(str + "T00:00:00");

const isValidIsoDate = (str: string): boolean =>
  str.length === 10 && !isNaN(parseDate(str).getTime());

type DraftState = {
  value: string;
  startInput: string;
  endInput: string;
  startDate: Date | null;
  endDate: Date | null;
};

const seedDraft = (
  value: ModuleDateRangeValue,
  presets: ModuleDateRangePreset[]
): DraftState => {
  const presetMatch =
    value.value !== CUSTOM_VALUE
      ? presets.find((p) => p.value === value.value && p.getDateRange)
      : undefined;
  const resolved = presetMatch?.getDateRange?.();
  const startStr = value.startDate ?? resolved?.startDate ?? "";
  const endStr = value.endDate ?? resolved?.endDate ?? "";
  return {
    value: value.value,
    startInput: startStr,
    endInput: endStr,
    startDate: startStr ? parseDate(startStr) : null,
    endDate: endStr ? parseDate(endStr) : null
  };
};

export const ModuleDateRangeSelect: FC<{
  value: ModuleDateRangeValue;
  onChange: (next: ModuleDateRangeValue) => void;
  presets?: ModuleDateRangePreset[];
  disabled?: boolean;
}> = ({ value, onChange, presets = MODULE_DATE_PRESETS, disabled }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftState>(() =>
    seedDraft(value, presets)
  );

  const resetDraft = () => setDraft(seedDraft(value, presets));

  // Keep draft synced with value while the popover is closed, and reseed on
  // the closed→open transition. This catches external value changes (e.g.
  // switching custom-dashboard tabs swaps the persisted range underneath
  // us) without clobbering in-progress edits during an open session.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || !wasOpen) setDraft(seedDraft(value, presets));
  }, [open, value, presets]);

  const handlePresetClick = (preset: ModuleDateRangePreset) => {
    if (preset.isNone) {
      setDraft({
        value: preset.value,
        startInput: "",
        endInput: "",
        startDate: null,
        endDate: null
      });
      return;
    }
    if (preset.isCustom) {
      setDraft((prev) => ({ ...prev, value: CUSTOM_VALUE }));
      return;
    }
    if (!preset.getDateRange) {
      setDraft((prev) => ({ ...prev, value: preset.value }));
      return;
    }
    const { startDate, endDate } = preset.getDateRange();
    setDraft({
      value: preset.value,
      startInput: startDate,
      endInput: endDate,
      startDate: parseDate(startDate),
      endDate: parseDate(endDate)
    });
  };

  // No value flip in date handlers — the radio's checked state is derived
  // from the current dates below, which means the prop-sync echo coming
  // back from DateInput / DatePickerComp on initial mount can't push the
  // selection into custom mode by accident.
  const handleCalendarChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    const startInput = start ? formatDate(start) : "";
    const endInput = end ? formatDate(end) : "";
    setDraft((prev) => {
      if (startInput === prev.startInput && endInput === prev.endInput)
        return prev;
      return {
        value: prev.value,
        startInput,
        endInput,
        startDate: start,
        endDate: end
      };
    });
  };

  const handleStartInputChange = (text: string) => {
    setDraft((prev) => {
      if (text === prev.startInput) return prev;
      return {
        ...prev,
        startInput: text,
        startDate: isValidIsoDate(text) ? parseDate(text) : prev.startDate
      };
    });
  };

  const handleEndInputChange = (text: string) => {
    setDraft((prev) => {
      if (text === prev.endInput) return prev;
      return {
        ...prev,
        endInput: text,
        endDate: isValidIsoDate(text) ? parseDate(text) : prev.endDate
      };
    });
  };

  const isNonePreset = (val: string) =>
    presets.find((p) => p.value === val)?.isNone === true;

  // Derive which preset reads as "selected" from the current draft dates.
  // Explicit Custom selection wins; otherwise we match dates against each
  // preset's getDateRange. If nothing matches, fall back to Custom.
  const checkedPresetValue = ((): string => {
    if (draft.value === CUSTOM_VALUE) return CUSTOM_VALUE;
    if (!draft.startInput && !draft.endInput) {
      const none = presets.find((p) => p.isNone);
      if (none && draft.value === none.value) return none.value;
    }
    for (const p of presets) {
      if (p.isCustom || p.isNone || !p.getDateRange) continue;
      const r = p.getDateRange();
      if (r.startDate === draft.startInput && r.endDate === draft.endInput)
        return p.value;
    }
    return CUSTOM_VALUE;
  })();

  const canApply =
    isNonePreset(checkedPresetValue) ||
    (Boolean(draft.startDate && draft.endDate) &&
      isValidIsoDate(draft.startInput) &&
      isValidIsoDate(draft.endInput));

  const handleCancel = () => {
    resetDraft();
    setOpen(false);
  };

  const handleApply = () => {
    if (!canApply) return;
    if (isNonePreset(checkedPresetValue))
      onChange({ value: checkedPresetValue });
    else
      onChange({
        value: checkedPresetValue,
        startDate: draft.startInput,
        endDate: draft.endInput
      });
    setOpen(false);
  };

  return (
    <Popover
      placement="bottom-start"
      open={open && !disabled}
      onOpenChange={(next) => {
        if (disabled) return;
        if (!next) resetDraft();
        setOpen(next);
      }}
    >
      <PopoverTrigger>
        <Button
          size={ButtonSize.SM}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
        >
          <div className="flex items-center gap-2">
            <span className="text-content-200">Date range:</span>{" "}
            {moduleDateRangeLabel(value, presets)}
            <HugeiconsIcon
              icon={open ? ArrowUp01Icon : ArrowDown01Icon}
              size={16}
            />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-10">
        <Card className="bg-white w-[600px] lg:w-[1200px] min-h-[440px] flex flex-col">
          <div className="flex flex-1 min-h-0">
            <div className="px-6 space-y-4 py-4 w-[200px] shrink-0 border-r border-alpha-40">
              {presets.map((preset) => (
                <Radio
                  id={`module-date-range-${preset.value}`}
                  name="moduleDateRangePreset"
                  key={preset.value}
                  className="w-full rounded-lg px-2 py-1.5 hover:bg-alpha-40"
                  label={preset.label}
                  checked={preset.value === checkedPresetValue}
                  onChange={() => handlePresetClick(preset)}
                />
              ))}
            </div>
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex gap-[14px] border-b border-alpha-40 items-center px-6 py-4">
                <DateInput
                  id="module-date-range-start"
                  hideOptional
                  label="From"
                  value={draft.startInput}
                  onChange={(e) => handleStartInputChange(e.target.value)}
                />
                <p>-</p>
                <DateInput
                  id="module-date-range-end"
                  hideOptional
                  label="To"
                  value={draft.endInput}
                  onChange={(e) => handleEndInputChange(e.target.value)}
                />
              </div>
              <div className="flex-1 lg:block hidden">
                <DatePickerComp
                  selected={draft.startDate}
                  onChange={handleCalendarChange}
                  monthsShown={2}
                  startDate={draft.startDate}
                  endDate={draft.endDate}
                  selectsRange
                  inline
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 border-t border-alpha-40 px-6 py-4">
            <Button size={ButtonSize.SM} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.PRIMARY}
              disabled={!canApply}
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </Card>
      </PopoverContent>
    </Popover>
  );
};
